import { Base, Component, Emit, Prop } from '@/vue-property-decorator'
import { SyntheticEvent } from '@/vue-tsx-support/types/dom'
import { useI18n } from '@/i18n'
import style from './style.module.scss'

type Props = {
  multiple?: boolean
  uploadShape?: UploadShape
  text?: string
  previewUrl?: string
  multimedia?: boolean
  btnCenter?: boolean
  supportTypeList?: string[]
  imgLimit?: number
}

export enum UploadErrorEnum {
  SIZE_EXCEEDS = 1,
  FILE_TYPE_EXCLUDE = 2,
  FILE_NULL = 3
}

export type UploadPayload = {
  file?: File
  error?: UploadErrorEnum
}

export enum UploadShape {
  ICON,
  BUTTON
}
/** 默認允许的文件类型 */
const DEFAULT_TYPE_LIST = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif']

const DEFAULT_IMG_LIMIT = 5 * Math.pow(2, 20)
@Component<ComUploadComp>({ name: 'ComUploadComp' })
export default class ComUploadComp extends Base<
  unknown,
  Props,
  {
    onChange: UploadPayload
  }
> {
  get sizeLimit() {
    /**支持影片格式可50mb、一般格式2mb */
    return this.multimedia
      ? 50 * Math.pow(2, 20)
      : this.imgLimit ?? DEFAULT_IMG_LIMIT
  }

  get typeList() {
    const list = this.supportTypeList ?? DEFAULT_TYPE_LIST
    if (this.multimedia) list.push('video/mp4')
    return list
  }
  /** 允许的文件扩展名 */
  get allowedExtensions() {
    return this.typeList.map((el) => `${el.split('/')[1]}`)
  }

  @Prop({ default: false })
  multiple!: Props['multiple']

  @Prop({ default: false })
  multimedia!: Props['multimedia']

  @Prop({ default: false })
  btnCenter!: Props['btnCenter']

  @Prop({ default: () => UploadShape.ICON })
  uploadShape!: Props['uploadShape']

  @Prop({ default: () => useI18n().t('lobby.common.upload.text') })
  text!: Props['text']

  @Prop({ default: '' })
  previewUrl!: Props['previewUrl']

  @Prop()
  supportTypeList!: Props['supportTypeList']

  @Prop({ default: () => DEFAULT_IMG_LIMIT })
  imgLimit!: Props['imgLimit']

  private fileType = ''
  //点击时移除上次的文件，避免选择相同文件时不触发上传
  private handlerClick(e: MouseEvent) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    e.target.value = ''
  }
  @Emit('change')
  private async handleChange(e: SyntheticEvent<HTMLInputElement>) {
    const files = e.target.files ?? []
    if ((files?.length ?? 0) < 0) {
      return {
        error: UploadErrorEnum.FILE_NULL
      }
    }

    const file = files[0]
    const getFileExtension = (filename: string) => {
      return filename.split('.').pop() ?? ''
    }
    if (file.size > this.sizeLimit!) {
      return {
        error: UploadErrorEnum.SIZE_EXCEEDS
      }
    }
    if (
      !this.typeList.includes(file.type) ||
      !this.allowedExtensions.includes(getFileExtension(file.name))
    ) {
      return {
        error: UploadErrorEnum.FILE_TYPE_EXCLUDE
      }
    }

    this.fileType = file.type

    return {
      file
    }
  }
  private renderPreview() {
    if (/image/g.test(this.previewUrl || '') || /image/g.test(this.fileType)) {
      return <img class={style.preview} src={this.previewUrl} />
    } else if (
      /video/g.test(this.previewUrl || '') ||
      /video/g.test(this.fileType)
    ) {
      return <video class={style.preview} src={this.previewUrl} />
    }
  }

  render() {
    switch (this.uploadShape) {
      case UploadShape.ICON:
        return (
          <div class={style.uploadWrap}>
            <div class={style.upload}>
              <span class={style.column}></span>
              <span class={style.row}></span>
              <input
                type="file"
                class={style.input}
                multiple={this.multiple}
                accept={this.typeList.join(',')}
                onChange={this.handleChange}
                onClick={this.handlerClick}
              />
            </div>
            {this.$slots?.btnText}
          </div>
        )
      case UploadShape.BUTTON:
        return (
          <div class={{ [style.btnCenter]: this.btnCenter! }}>
            <div class={style.upload}>{this.renderPreview()}</div>
            <div>
              {this.$slots?.btnText}
              <div
                class={{
                  [style.btn]: true,
                  globalBtn: true
                }}
              >
                <span>{this.text}</span>
                <input
                  type="file"
                  class={style.input}
                  multiple={this.multiple}
                  accept={this.typeList.join(',')}
                  onChange={this.handleChange}
                  onClick={this.handlerClick}
                />
              </div>
            </div>
          </div>
        )
      default:
        return <div></div>
    }
  }
}
